import type {
    OpxRecurrencePattern,
    OpxDailyRecurrencePattern,
    OpxWeeklyRecurrencePattern,
    OpxAbsoluteMonthlyRecurrencePattern,
    OpxRelativeMonthlyRecurrencePattern,
    OpxAbsoluteYearlyRecurrencePattern,
    OpxRelativeYearlyRecurrencePattern,
    OpxRecurrenceRange,
    OpxNoEndRecurrenceRange,
    OpxNumberedRecurrenceRange,
    OpxEndDateRecurrenceRange,
    OpxRecurrence,
    OpxEvent,
} from '../store/schema/RoomPanelOpxTypes';

import { OwaDate, createDisplayDate, userDate, utcDate } from 'owa-datetime';
import type { CalendarEventTime } from '../components/RoomSelector/RoomSelector.types';

import type RecurrenceType from 'owa-service/lib/contract/RecurrenceType';

import type RecurrencePatternBaseType from 'owa-service/lib/contract/RecurrencePatternBaseType';
import type DailyRecurrence from 'owa-service/lib/contract/DailyRecurrence';
import type WeeklyRecurrence from 'owa-service/lib/contract/WeeklyRecurrence';
import type AbsoluteMonthlyRecurrence from 'owa-service/lib/contract/AbsoluteMonthlyRecurrence';
import type RelativeMonthlyRecurrence from 'owa-service/lib/contract/RelativeMonthlyRecurrence';
import type AbsoluteYearlyRecurrence from 'owa-service/lib/contract/AbsoluteYearlyRecurrence';
import type RelativeYearlyRecurrence from 'owa-service/lib/contract/RelativeYearlyRecurrence';

import type RecurrenceRangeBaseType from 'owa-service/lib/contract/RecurrenceRangeBaseType';
import type NoEndRecurrence from 'owa-service/lib/contract/NoEndRecurrence';
import type NumberedRecurrence from 'owa-service/lib/contract/NumberedRecurrence';
import type EndDateRecurrence from 'owa-service/lib/contract/EndDateRecurrence';

import {
    DAILY_RECURRENCE_TYPE_NAME,
    WEEKLY_RECURRENCE_TYPE_NAME,
    ABSOLUTE_MONTHLY_RECURRENCE_TYPE_NAME,
    RELATIVE_MONTHLY_RECURRENCE_TYPE_NAME,
    ABSOLUTE_YEARLY_RECURRENCE_TYPE_NAME,
    RELATIVE_YEARLY_RECURRENCE_TYPE_NAME,
    MONTHS_OF_YEAR,
    NO_END_RECURRENCE,
    NUMBERED_RECURRENCE,
    END_DATE_RECURRENCE,
} from 'owa-recurrence-utils/lib/constants';
import type AttendeeType from 'owa-service/lib/contract/AttendeeType';

function convertDate(utc: string): OwaDate {
    const date = utc && new Date(utc);
    // Ideally these dates should use the OPX Host timezone, but there doesn't seem to be
    // a way to reliably convert from a timezone definition to an OWA timezone ID.
    return (
        (date &&
            userDate(
                utcDate(
                    date.getUTCFullYear(),
                    date.getUTCMonth(),
                    date.getUTCDate(),
                    date.getUTCHours(),
                    date.getUTCMinutes()
                )
            )) ||
        createDisplayDate(2017, 6, 30) // this is a hack for now.
    );
}

function convertRecurrencePattern(pattern: OpxRecurrencePattern): RecurrencePatternBaseType {
    switch (pattern.Type) {
        case 'Daily':
            const daily = pattern as OpxDailyRecurrencePattern;
            return {
                __type: DAILY_RECURRENCE_TYPE_NAME,
                Interval: daily.Interval,
            } as DailyRecurrence;
        case 'Weekly':
            const weekly = pattern as OpxWeeklyRecurrencePattern;
            return {
                __type: WEEKLY_RECURRENCE_TYPE_NAME,
                Interval: weekly.Interval,
                DaysOfWeek: weekly.DaysOfWeek.join(' '),
                FirstDayOfWeek: weekly.FirstDayOfWeek,
            } as WeeklyRecurrence;
        case 'AbsoluteMonthly':
            const absoluteMonthly = pattern as OpxAbsoluteMonthlyRecurrencePattern;
            return {
                __type: ABSOLUTE_MONTHLY_RECURRENCE_TYPE_NAME,
                Interval: absoluteMonthly.Interval,
                DayOfMonth: absoluteMonthly.DayOfMonth,
            } as AbsoluteMonthlyRecurrence;
        case 'RelativeMonthly':
            const relativeMonthly = pattern as OpxRelativeMonthlyRecurrencePattern;
            return {
                __type: RELATIVE_MONTHLY_RECURRENCE_TYPE_NAME,
                Interval: relativeMonthly.Interval,
                DaysOfWeek: relativeMonthly.DaysOfWeek.join(' '),
                DayOfWeekIndex: relativeMonthly.Index || 'None',
            } as RelativeMonthlyRecurrence;
        case 'AbsoluteYearly':
            const absoluteYearly = pattern as OpxAbsoluteYearlyRecurrencePattern;
            return {
                __type: ABSOLUTE_YEARLY_RECURRENCE_TYPE_NAME,
                Interval: absoluteYearly.Interval,
                DayOfMonth: absoluteYearly.DayOfMonth,
                Month: MONTHS_OF_YEAR[absoluteYearly.Month - 1],
            } as AbsoluteYearlyRecurrence;
        case 'RelativeYearly':
            const relativeYearly = pattern as OpxRelativeYearlyRecurrencePattern;
            return {
                __type: RELATIVE_YEARLY_RECURRENCE_TYPE_NAME,
                Interval: relativeYearly.Interval,
                DaysOfWeek: relativeYearly.DaysOfWeek.join(' '),
                DayOfWeekIndex: relativeYearly.Index || 'None',
                Month: MONTHS_OF_YEAR[relativeYearly.Month - 1],
            } as RelativeYearlyRecurrence;
    }

    return null;
}

function convertRecurrenceRange(range: OpxRecurrenceRange): RecurrenceRangeBaseType {
    switch (range.Type) {
        case 'NoEnd':
            const noEnd = range as OpxNoEndRecurrenceRange;
            return { __type: NO_END_RECURRENCE, StartDate: noEnd.StartDate } as NoEndRecurrence;
        case 'Numbered':
            const numbered = range as OpxNumberedRecurrenceRange;
            return {
                __type: NUMBERED_RECURRENCE,
                StartDate: numbered.StartDate,
                NumberOfOccurrences: numbered.NumberOfOccurrences,
            } as NumberedRecurrence;
        case 'EndDate':
            const endDate = range as OpxEndDateRecurrenceRange;
            return {
                __type: END_DATE_RECURRENCE,
                StartDate: endDate.StartDate,
                EndDate: endDate.EndDate,
            } as EndDateRecurrence;
    }

    return null;
}

function convertRecurrence(recurrence: OpxRecurrence): RecurrenceType {
    return (
        recurrence && {
            RecurrencePattern: convertRecurrencePattern(recurrence.Pattern),
            RecurrenceRange: convertRecurrenceRange(recurrence.Range),
        }
    );
}

function convertAttendees(addresses: string[]): AttendeeType[] {
    if (!addresses) {
        return [];
    }
    return addresses
        .filter(address => !!address)
        .map(address => ({ Mailbox: { EmailAddress: address } }));
}

export function convertOpxEvent(data: OpxEvent): CalendarEventTime {
    if (!data) {
        return {
            Start: null,
            End: null,
            IsAllDayEvent: false,
            Recurrence: null,
            RequiredAttendees: [],
            OptionalAttendees: [],
        };
    }
    return {
        Start: convertDate(data.utcStart),
        End: convertDate(data.utcEnd),
        IsAllDayEvent: data.isAllDay === true,
        Recurrence: convertRecurrence(data.recurrence),
        RequiredAttendees: convertAttendees(data.requiredAttendees),
        OptionalAttendees: convertAttendees(data.optionalAttendees),
    };
}
